import React, { memo, useEffect } from "react";
import jsPDF from "jspdf";
import moment from "moment";
import { addPageNumber } from "../common/addPageNumber";
import autoTable from "jspdf-autotable";

interface TimeSheetAggregate {
  data: any;
  setPdfBlob: (blob: Blob) => void;
  formData: any;
}

const TimeSheetAggregate: React.FC<TimeSheetAggregate> = ({
  data,
  setPdfBlob,
  formData,
}) => {
  const startDate = formData?.StartDate
    ? moment(formData?.StartDate).format("MM-DD-YYYY")
    : null;
  const endDate = formData?.EndDate
    ? moment(formData?.EndDate).format("MM-DD-YYYY")
    : null;

  const generatePDF = () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    let y = margin;
    // Title
    doc.setFontSize(16);
    doc.text("Timesheet Aggregate Report", 10, 13);
    y += 10;
    doc.setFontSize(9);
    doc.text("Date Range", margin + 4, y);
    doc.text(`${startDate ?? ""} - ${endDate ?? ""}`, margin + 30, y);
    y += 4;

    // Define columns for the report
    const columns = [
      { header: "Employee name", dataKey: "FULLNAME" },
      { header: "District Name", dataKey: "DIST_NAM" },
      { header: "School Name", dataKey: "SCHOOL_NAME" },
      { header: "Site Name", dataKey: "SITE_NAM" },
      { header: "Budget Code", dataKey: "SITE_NUM" },
      { header: "Total Hours Present", dataKey: "WorkingHours" },
      { header: "Max Hours", dataKey: "Allocated_Hours" },
      { header: "Total Hours Absent", dataKey: "Total_Hours_Absent" },
      { header: "Absent Reason", dataKey: "Absent_Reason" },
    ];
    const roundToQuarter = (value: number) => {
      return Math.round(value * 4) / 4;
    };
    // Format the data for autoTable
    const tableData = data.timesheetAggregateData.map((item: any) => ({
      FULLNAME: `${item.FIRSTNAME || ""}, ${item.LASTNAME || ""}`, // Handle potential nulls
      DIST_NAM: item.DIST_NAM || "", // Default to empty string if null or undefined
      SCHOOL_NAME: item.SCH_NAM || "", // Default to empty string if null or undefined
      SITE_NAM: item.SITE_NAM || "", // Default to empty string if null or undefined
      SITE_NUM: item.SITE_NUM || "",
      WorkingHours: roundToQuarter(item.WorkingHours),
      Allocated_Hours: roundToQuarter(item.Allocated_Hours),
      Total_Hours_Absent: roundToQuarter(item.TotalAbsence),
      Absent_Reason: item.AbsenceDetails || "",
    }));

    const totalWorkingHours = tableData.reduce(
      (total: any, item: any) => total + (item.WorkingHours || 0),
      0
    );
    const totalAllocatedHours = tableData.reduce(
      (total: any, item: any) => total + (item.Allocated_Hours || 0),
      0
    );
    const totalAbsenceHour = tableData.reduce(
      (total: any, item: any) => total + (item.Total_Hours_Absent || 0),
      0
    );
    // Add the total row at the end of the tableData
    tableData.push({
      FULLNAME: "Total",
      DIST_NAM: "",
      SCHOOL_NAME: "",
      SITE_NAM: "",
      SITE_NUM: "",
      WorkingHours: totalWorkingHours,
      Allocated_Hours:totalAllocatedHours,
      Total_Hours_Absent: totalAbsenceHour,
      Absent_Reason: "",
    });
    // Generate the table using autoTable
    autoTable(doc, {
      head: [columns.map((col) => col.header)],
      body: tableData.map(
        (row: { [s: string]: unknown } | ArrayLike<unknown>) =>
          Object.values(row)
      ),
      theme: "plain",
      startY: y,
      styles: {
        fontSize: 6,
        cellPadding: 1,
        lineColor: 40,
        lineWidth: 0.1,
      },
      headStyles: {
        fontStyle: "bold",
        fontSize: 7,
        textColor: 0,
        fillColor: false,
        halign: "center",
      },
      bodyStyles: {
        lineWidth: 0,
        // halign: 'center',
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 20 }, // Name
        1: { cellWidth: 20 }, // District
        2: { cellWidth: 20 }, // school
        3: { cellWidth: 20 }, // sitr
        4: { cellWidth: 15 }, // sitr
        5: { cellWidth: 15 }, // WorkingHours
        6: { cellWidth: 15 }, // allocated
        7: { cellWidth: 15 }, // absence
        8: { cellWidth: 50 }, // reasons
      },
      didDrawCell: (data: {
        section: string;
        cell: { x: number; y: number; width: any; height: any };
      }) => {
        doc.setDrawColor(0);
        doc.setLineWidth(0.2);
        doc.line(
          data.cell.x,
          data.cell.y,
          data.cell.x + data.cell.width,
          data.cell.y
        ); // Top
        doc.line(
          data.cell.x,
          data.cell.y + data.cell.height,
          data.cell.x + data.cell.width,
          data.cell.y + data.cell.height
        ); // Bottom
        doc.line(
          data.cell.x,
          data.cell.y,
          data.cell.x,
          data.cell.y + data.cell.height
        ); // Left
        doc.line(
          data.cell.x + data.cell.width,
          data.cell.y,
          data.cell.x + data.cell.width,
          data.cell.y + data.cell.height
        ); // Right
      },
      didDrawPage: (data: { table: { body: string | any[] } }) => {
        // Draw bottom border for the last row
        if (data.table.body && data.table.body.length > 0) {
          const lastRow = data.table.body[data.table.body.length - 1];
          if (lastRow && lastRow.cells && lastRow.cells.length > 0) {
            const firstCell = lastRow.cells[0];
            const lastCell = lastRow.cells[lastRow.cells.length - 1];
            if (firstCell && lastCell) {
              doc.setLineWidth(0.3);
              doc.line(
                firstCell.x,
                firstCell.y + firstCell.height,
                lastCell.x + lastCell.width,
                lastCell.y + lastCell.height
              );
            }
          }
        }
      },
    });

    addPageNumber(doc);
    const pdfBlob = doc.output("blob");
    setPdfBlob(pdfBlob);
  };

  useEffect(() => {
    if (data) {
      generatePDF();
    }
  }, [data]);

  return null; // No visual component, generates the PDF automatically
};
export default memo(TimeSheetAggregate);
